import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Link,
} from "@mui/material";
import { useFormik } from "formik";
import store from "../../store";
import { showAlert, showError } from "../../store/actions/feedback";
import Loader from "../common/Loader";
import {
  getPdfDocument,
  updateSign,
} from "../../services/api-services/eSignService";
import { getAllForms } from "../../services/api-services/reviewInfoService";
import SendDetailsToEmployer from "../common/ui/SendDetailsToEmployer";
import { PdfResponse, ESignForm, ESignFormData } from "./models/user-interface";
import { SET_IS_ENABLE_SEND_TO_EMPLOYER } from "../../store/actions/eSign";
import { connect } from "react-redux";

enum FORMS {
  DIRECT_DEPOSIT = "employee_direct_deposit",
  IRS_FORM = "US_W-4",
}
function ESign(props: any) {
  const { employee } = props;
  const [isDisableCheckbox, setIsDisableCheckbox] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState<ESignForm[]>();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [directDepositPdf, setDirectDepositPdf] = useState("");
  const [irsPdf, setIrsPdf] = useState("");
  const [isAllFormsAuthed, setIsAllFormsAuthed] = useState(false);
  const mountedRef = useRef(true);
  const [employeeFullName, setEmployeeFullName] = useState("");
  const { handleChange, values, setFieldValue } = useFormik({
    initialValues: { termsOfService: false, signature: "" },
    onSubmit: (values) => {},
  });
  const sendEnableSendToEmployerInStore = (isEnable: boolean) => {
    store.dispatch({
      type: SET_IS_ENABLE_SEND_TO_EMPLOYER,
      state: isEnable,
    });
  };
  const checkboxHandleChange = (e) => {
    const value = e.target.checked;
    setFieldValue("termsOfService", value);
  };
  const showErrorMessage = (error) => {
    const errorMessage = error.status?.message;
    store.dispatch(
      showAlert({
        alertType: "error",
        alertText: errorMessage,
      })
    );
  };
  useEffect(() => {
    if (!employee) return;
    const fullName =
      employee.first_name +
      " " +
      employee?.middle_initial +
      " " +
      employee.last_name;
    setEmployeeFullName(fullName);
  }, [employee]);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const preSelectTnC = () => {
    setFieldValue("termsOfService", true);
    setIsDisableCheckbox(true);
  };
  const checkIfSignatureIsRequired = (employeeData: ESignForm[]) => {
    let isRequiredSigning = true;
    for (const employee of employeeData) {
      if (!employee.requires_signing) {
        isRequiredSigning = false;
        break;
      }
    }
    if (isRequiredSigning) {
      setIsAllFormsAuthed(false);
    } else {
      preSelectTnC();
    }
  };
  const checkIfAllFormsAuthed = (employeeData: ESignForm[]) => {
    let isRequiredSigning = false;
    for (const employee of employeeData) {
      if (employee.requires_signing) {
        isRequiredSigning = true;
        break;
      }
    }
    if (isRequiredSigning) {
      sendEnableSendToEmployerInStore(false);
      setIsAllFormsAuthed(false);
    } else {
      setIsAllFormsAuthed(true);
      sendEnableSendToEmployerInStore(true);
    }
  };
  useEffect(() => {
    getAllForms()
      .then((response: any) => {
        if (!mountedRef.current) return null;
        setEmployeeData(response.data);
        checkIfSignatureIsRequired(response.data);
        checkIfAllFormsAuthed(response.data);
        fetchDocumentLinks(response.data);
      })
      .catch((error) => {
        if (!mountedRef.current) return null;
        setIsLoading(false);
        showErrorMessage(error);
      });
  }, []);

  const sendToEmployer = () => {
    setShowConfirmModal(true);
  };

  const onModalClose = () => {
    setShowConfirmModal(false);
  };

  const getPdfDocumentFromApi = (uuid) => {
    return getPdfDocument(uuid);
  };
  const setDocumentLinks = (pdfResponseArray) => {
    pdfResponseArray.forEach((pdfData) => {
      const data: PdfResponse = pdfData?.data;
      if (data.document_url.includes(FORMS.DIRECT_DEPOSIT)) {
        setDirectDepositPdf(data.document_url);
      } else {
        setIrsPdf(data.document_url);
      }
    });
  };
  const fetchDocumentLinks = (employeeData) => {
    const allPdfRequests: any = [];
    for (const employee of employeeData) {
      const sendSignaturePromise = getPdfDocumentFromApi(employee?.uuid);
      allPdfRequests.push(sendSignaturePromise);
    }
    Promise.all(allPdfRequests)
      .then((res) => {
        setIsLoading(false);
        setDocumentLinks(res);
      })
      .catch((error) => {
        setIsLoading(false);
        showErrorMessage(error);
      });
  };
  const removeSpecialCharacters = (name: string): string => {
    const regex = /[^a-zA-Z ]/g;
    return name.replace(regex, "");
  };
  const getFormSignatureData = () => {
    const postData = {
      signature_text: removeSpecialCharacters(employeeFullName) ?? "",
      agree: true,
    };
    return postData;
  };

  const digitallySignForm = (index: number) => {
    if (!employeeData) return;
    const employeeUuid = employeeData[index].uuid;
    if (!employeeUuid) return;

    const formData = getFormSignatureData();
    setIsLoading(true);
    updateSign(formData as ESignFormData, employeeUuid)
      .then(() => {
        employeeData[index].requires_signing = false;
        setIsLoading(false);
        checkIfSignatureIsRequired(employeeData);
        checkIfAllFormsAuthed(employeeData);
      })
      .catch((error) => {
        const errorMessage = error.status?.message;
        setIsLoading(false);
        showError(errorMessage);
      });
  };
  const onDigitallySignClick = (index: number) => {
    digitallySignForm(index);
  };
  const createFormFields = () => {
    if (!employeeData || !employeeData.length) return;
    return employeeData.map((formData, index) => {
      if (formData.name === FORMS.DIRECT_DEPOSIT) {
        return (
          <div className="e-sign__direct-deposit" key={index}>
            <Typography className="" variant="h6">
              Direct Deposit Authorization
              <span className="required">(Required)</span>
            </Typography>
            <p>
              Authorizes the transfer of funds from your business bank account
              to pay the wages of your employees.
            </p>

            <Link
              href={directDepositPdf}
              target="_blank"
              className="link-button-secondary"
            >
              View Document
            </Link>

            {formData.requires_signing && (
              <Button
                variant="contained"
                className="btn-tertiary"
                disabled={!values.termsOfService}
                onClick={() => onDigitallySignClick(index)}
              >
                Digitally Sign
              </Button>
            )}
            {!formData.requires_signing && (
              <p className="e-sign__digitally-signed">Digitally Signed</p>
            )}
          </div>
        );
      } else if (formData.name === FORMS.IRS_FORM) {
        return (
          <div className="e-sign__irs" key={index}>
            <Typography className="" variant="h6">
              IRS Form W-4 <span className="required">(Required)</span>
            </Typography>
            <p>
              Authorizes your employer to be able to withhold the correct
              Federal income tax from your pay. Consider completing a new Form
              W-4 each year, or when your personal or financial situation
              changes.
            </p>

            <Link
              href={irsPdf}
              target="_blank"
              className="link-button-secondary"
            >
              View Document
            </Link>
            {formData.requires_signing && (
              <Button
                variant="contained"
                className="btn-tertiary"
                disabled={!values.termsOfService}
                onClick={() => onDigitallySignClick(index)}
              >
                Digitally Sign
              </Button>
            )}
            {!formData.requires_signing && (
              <p className="e-sign__digitally-signed">Digitally Signed</p>
            )}
          </div>
        );
      }
    });
  };
  const setHTMLBasedOnEmployeeData = () => {
    const formNodes = createFormFields();
    if (!formNodes) return;
    return formNodes;
  };
  return (
    <div className="e-sign user-details--section">
      {showConfirmModal && <SendDetailsToEmployer onClose={onModalClose} />}

      {isLoading ? (
        <Loader />
      ) : (
        <>
          {employeeData ? (
            <>
              <div className="e-sign__electronic-signature">
                <div
                  className={isDisableCheckbox ? "e-sign__disable-fields" : ""}
                >
                  <div className="e-sign__terms-conditions">
                    <FormControlLabel
                      control={<Checkbox />}
                      disabled={isDisableCheckbox}
                      checked={values.termsOfService}
                      label="Agree to use an Electronic Signature"
                      name="termsOfService"
                      onChange={checkboxHandleChange}
                    />
                    <span className="required">(Required)</span>
                  </div>
                  <div className="e-sign__terms-conditions-sign">
                    <span className="sign-label">Signature</span>
                    <span className="name">{employeeFullName}</span>
                    <span>
                      <p>
                        Your digital signature is the same as the name you
                        provided in the Personal Info section.
                      </p>
                    </span>
                  </div>
                </div>
                {setHTMLBasedOnEmployeeData()}
              </div>
              <Button
                variant="contained"
                className="btn-primary send-to-employer"
                disabled={!isAllFormsAuthed}
                onClick={sendToEmployer}
              >
                Send to Employer
              </Button>
            </>
          ) : (
            "You need to complete your onboarding Process before E-Sign."
          )}
        </>
      )}
    </div>
  );
}
const mapStateToProps = (state) => ({
  employee: state.employee.details,
});
export default connect(mapStateToProps)(ESign);
